import { type Database } from "@fitness-app/supabase";

import { type BusinessEventType } from "../businessEvents/domain/businessEventTypes";
import { type ProductEventType } from "../businessEvents/domain/product/ProductEvents";
import { type TraineeEventType } from "../businessEvents/domain/trainee/TraineeEvents";
import { type CustomerFeatureName } from "./CustomerFeature";
import { type EmailClient, type EmbeddedForm } from "./ProductsSettings";

export type WebhookLogEntity = Database["public"]["Tables"]["webhook_log"]["Row"];

export enum ProductIntegrationClient {
  Discord = "discord",
  Slack = "slack",
}

export enum SubscriberIntegrationClient {
  Discord = "discord",
}
export interface ConstantMessage {
  type: "firstDayOfSignIn";
  message: string;
}

export interface EmailList {
  id: string;
  name: string;
  client: EmailClient;
  account: string;
}

export type SpecialParamValue =
  | "productId"
  | "price"
  | "priceId"
  | "clientEmail"
  | "clientFirstName"
  | "clientLastName"
  | "clientName"
  | "paymentType"
  | "productName"
  | "orderId";

export interface NumberFieldValue {
  type: "number";
  value: number | SpecialParamValue;
}

export interface StringFieldValue {
  type: "string";
  value: string | SpecialParamValue;
}

export enum AuthType {
  BasicAuth = "basicAuth",
  BearerToken = "bearerToken",
  ApiKey = "apiKey",
  None = "none",
}

export interface BasicAuth {
  username: string;
  password: string;
  type: AuthType.BasicAuth;
}

export interface BearerToken {
  token: string;
  type: AuthType.BearerToken;
}

export interface APIKey {
  key: string;
  value: string;
  addTo: "header" | "query";
  type: AuthType.ApiKey;
}

export type AuthTypeOption = BasicAuth | BearerToken | APIKey;

export interface BaseWebhook {
  triggers: BusinessEventType[];
  method: "get" | "post";
  url: string;
  description?: string;
  auth: null | AuthTypeOption;
  active: boolean;
  id: string;
  payload?: Record<string, any>;
}

export interface PostWebhook extends BaseWebhook {
  method: "post";
  payload: Record<string, StringFieldValue | NumberFieldValue>;
  payloadAsParams: boolean;
}

export interface GetWebhook extends BaseWebhook {
  method: "get";
  payload?: Record<string, SpecialParamValue | string>;
}

export type Webhook = PostWebhook | GetWebhook;

export enum TaskType {
  EmailClient = "emailClient",
  DiscordIntegration = "discordIntegration",
  FitnessApp = "fitnessApp",
  ProductTask = "productTask",
  TeamMemberTask = "teamMemberTask",
}

export enum EmailClientTask {
  AddTags = "add_tags",
  RemoveTags = "remove_tags",
  AddToLists = "add_to_lists",
  RemoveFromLists = "remove_from_lists",
  AddFieldValues = "add_field_values",
}

export enum DiscordIntegrationTask {
  AddToServer = "add_to_server",
  RemoveFromServer = "remove_from_server",
  AddRole = "add_role",
  RemoveRole = "remove_role",
}

export enum FitnessAppTask {
  AddClientToApp = "add_client_to_app",
  RemoveClientFromApp = "remove_client_from_app",
  AddProgramAutomationToClient = "add_automation_to_client",
  AddTagsToTraineeProfile = "add_tags_to_trainee_profile",
  RemoveTagsFromTraineeProfile = "remove_tags_from_trainee_profile",
  ActivatedCustomerFeature = "activated_customer_feature",
  DeactivatedCustomerFeature = "deactivated_customer_feature",
}

export enum TaskCreator {
  System = "system",
  Owner = "owner",
}

export enum ProductTask {
  ImportIntoProduct = "import_into_product",
  RemoveFromProduct = "remove_from_product",
}

export enum PriceFilterType {
  Included = "included",
  Excluded = "excluded",
}

export interface PriceFilter {
  type: PriceFilterType;
  prices: string[];
}

export enum CheckoutVariantFilterType {
  Included = "checkout_variant_included",
  Excluded = "checkout_variant_excluded",
}

export interface CheckoutVariantFilter {
  type: CheckoutVariantFilterType;
  checkoutVariants: string[];
}

export type AutomationFilter = PriceFilter | CheckoutVariantFilter;

export interface BaseTask {
  active: boolean;
  description?: string;
  id: string;
  protected?: boolean;
  creator?: TaskCreator;
  filters?: AutomationFilter[] | null;
}

export enum RefundType {
  None = "none",
  AllLastPayment = "allLastPayment",
  Manual = "manual",
  ProratedLastPayment = "proratedLastPayment",
}

/**
 * Where the granted access takes its period from (TRE-2110). `trigger_product`
 * = the access the client just got in the product this automation belongs to
 * (the add-on case) — no price to pick and the add-on ends with the bundle.
 * Absent on tasks saved before, which means `price`.
 */
export type ImportIntoProductAccessSource = "price" | "trigger_product";

export interface ImportIntoProductTask {
  type: ProductTask.ImportIntoProduct;
  products: {
    productId: string;
    priceId: string | null;
    accessSource?: ImportIntoProductAccessSource;
    /** Only with `trigger_product`: keep the add-on in step with the bundle's extensions and end. */
    followSource?: boolean;
  }[];
  sendEmailAboutAddToProduct?: boolean;
}

export interface RemoveFromProductTask {
  type: ProductTask.RemoveFromProduct;
  products: { productId: string }[];
  cancelActiveSubscription?: boolean;
  refundType?: RefundType;
}

export interface AddTagsTask {
  type: EmailClientTask.AddTags;
  tags: string[];
  emailClients: (TagsEmailClient | TagsEmailList)[];
}

export interface FieldValuesEmailList {
  id: string;
  name: string;
  client: EmailClient.MailChimp | EmailClient.GetResponse | EmailClient.FreshMail;
  account: string;
}

export interface TagsEmailList {
  id: string;
  name: string;
  client: EmailClient.MailChimp | EmailClient.GetResponse | EmailClient.FreshMail;
  account: string;
}

export interface TagsEmailClient {
  client: EmailClient.ActiveCampaign | EmailClient.ConvertKit | EmailClient.MailerLite;
  account: string;
}

export interface FieldValuesEmailClient {
  client: EmailClient.ActiveCampaign | EmailClient.ConvertKit | EmailClient.MailerLite;
  account: string;
}

export type TaskVariable = `{{${string}}}`;

export interface AddFieldValues {
  type: EmailClientTask.AddFieldValues;
  values: Record<string, string | number | TaskVariable>;
  emailLists: (FieldValuesEmailList | FieldValuesEmailClient)[];
}

export interface RemoveTagsTask {
  type: EmailClientTask.RemoveTags;
  tags: string[];
  emailClients: (TagsEmailClient | TagsEmailList)[];
}

export interface AddToListsTask {
  type: EmailClientTask.AddToLists;
  emailLists: EmailList[];
}

export interface RemoveFromListsTask {
  type: EmailClientTask.RemoveFromLists;
  emailLists: EmailList[];
}

export interface ProductAutomationTask extends BaseTask {
  triggers: BusinessEventTriggers[];
  type: TaskType.ProductTask;
  action: ImportIntoProductTask | RemoveFromProductTask;
}

export type BusinessEventTriggers =
  | ProductEventType.ProductPurchased
  | ProductEventType.ProductSubscriptionEnd
  | ProductEventType.ProductSubscriptionRenewed
  | ProductEventType.ProductAccessRenewed
  | ProductEventType.ProductAccessPeriodEnd
  | ProductEventType.ProductClientImported
  | ProductEventType.ProductAccessPeriodEndingIn14Days
  | ProductEventType.ProductAccessPeriodEndingIn7Days
  | ProductEventType.ProductAccessPeriodEndingIn3Days
  | ProductEventType.ProductAccessPeriodEndingIn1Day
  | ProductEventType.ProductClientArchived
  | ProductEventType.ProductClientRemoved
  | ProductEventType.ProductClientEmailChanged
  | ProductEventType.ProductAccessProlonged
  | ProductEventType.ProductAccessRescheduled
  | TraineeEventType.TraineeActivatedAccount;

export interface EmailClientAutomationTask extends BaseTask {
  triggers: BusinessEventTriggers[];
  type: TaskType.EmailClient;
  action: AddTagsTask | RemoveTagsTask | AddToListsTask | RemoveFromListsTask | AddFieldValues;
  sendNotificationToSlackChannel?: false | null | string | true;
}

export interface AddToServer {
  type: DiscordIntegrationTask.AddToServer;
  serverId: string;
}

export interface RemoveFromServer {
  type: DiscordIntegrationTask.RemoveFromServer;
  serverId: string;
}

export interface AddRole {
  type: DiscordIntegrationTask.AddRole;
  serverId: string;
  roleId: string;
  roleName: string;
}

export interface RemoveRole {
  type: DiscordIntegrationTask.RemoveRole;
  serverId: string;
  roleId: string;
  roleName: string;
}

export type EmailTaskGroup = AddTagsTask | RemoveTagsTask | AddToListsTask | RemoveFromListsTask | AddFieldValues;

export interface AddClientToFitnessApp {
  type: FitnessAppTask.AddClientToApp;
  programAutomation: null | {
    name: string;
    id: string;
    startAutomationFrom: AutomationStartPoint;
  };
  accessType: AppAccessType;
  tags: string[];
  sendReminderAboutAccess?:
    | false
    | {
        afterDays: number;
        actions: EmailTaskGroup[];
      }[];
}

export interface AddTagsToTraineeProfile {
  type: FitnessAppTask.AddTagsToTraineeProfile;
  tags: string[];
}

export interface RemoveTagsFromTraineeProfile {
  type: FitnessAppTask.RemoveTagsFromTraineeProfile;
  tags: string[];
}

export interface ActivatedCustomerFeature {
  type: FitnessAppTask.ActivatedCustomerFeature;
  features: CustomerFeatureName[];
}

export interface DeactivatedCustomerFeature {
  type: FitnessAppTask.DeactivatedCustomerFeature;
  features: CustomerFeatureName[];
}

export interface RemoveClientFromFitnessApp {
  type: FitnessAppTask.RemoveClientFromApp;
  accessType?: AppAccessType;
  programAutomation?: {
    name: string;
    id: string;
    startAutomationFrom: AutomationStartPoint;
  };
}

export interface AddProgramAutomationToClient {
  type: FitnessAppTask.AddProgramAutomationToClient;
  programAutomation: {
    name: string;
    id: string;
    startAutomationFrom: AutomationStartPoint;
  };
}

export interface DiscordAutomationTask extends BaseTask {
  triggers: BusinessEventTriggers[];
  type: TaskType.DiscordIntegration;
  action: AddToServer | RemoveFromServer | AddRole | RemoveRole;
}

export enum AppAccessType {
  FullAccess = "fullAccess",
  Limited = "limited",
}

export enum AutomationStartPoint {
  AfterPurchase = "afterPurchase",
  StartOfWeek = "startOfWeek",
  /** TRE-1603: schedule/diet/plans apply once the initial survey is answered (AI-first automations). */
  AfterInitialSurvey = "afterInitialSurvey",
}

export interface FitnessAppAutomationTask extends BaseTask {
  triggers: BusinessEventTriggers[];
  type: TaskType.FitnessApp;
  action:
    | AddClientToFitnessApp
    | RemoveClientFromFitnessApp
    | AddProgramAutomationToClient
    | AddTagsToTraineeProfile
    | RemoveTagsFromTraineeProfile
    | ActivatedCustomerFeature
    | DeactivatedCustomerFeature;
}

export type AutomationTask =
  | EmailClientAutomationTask
  | DiscordAutomationTask
  | FitnessAppAutomationTask
  | ProductAutomationTask;

export type AutomationTaskWithoutTriggers =
  | Omit<EmailClientAutomationTask, "triggers">
  | Omit<DiscordAutomationTask, "triggers">
  | Omit<FitnessAppAutomationTask, "triggers">;

export type ProductTaxSettings =
  | {
      vatRate: 0 | 4 | 5 | 7 | 8 | 23;
      legalVatExemption?: never;
      npTaxKind?: never;
    }
  | {
      vatRate: "zw";
      legalVatExemption: string;
      npTaxKind?: never;
    }
  | {
      vatRate: "np";
      legalVatExemption?: never;
      npTaxKind: "export_service" | "export_service_eu";
    };

export type GTUCode = `GTU_${number}`;

export interface ProductInvoiceConfig {
  flatTaxRate: number | null;
  itemName: string | null;
  taxSettings?: ProductTaxSettings | null;
  pkwiu?: string | null;
  gtu?: GTUCode | null;
}

export interface ProductCartSettings {
  features?: string[];
  customButtonLabel?: string;
  preSaleUrl?: string;
  hidePriceWhenPreSaleLinkExists?: boolean;
  useTPayBlik?: boolean;
  /** Enables BLIK recurring (model A) for installment prices in the checkout. */
  useTPayBlikRecurring?: boolean;
  /**
   * Raty opisane w koszyku jako zobowiązanie na cały program: „N rat × X, łącznie Y,
   * ostatnia rata DD.MM” plus obowiązkowa zgoda (TRE-2170). Domyślnie wyłączone.
   */
  installmentsContractFraming?: boolean;
  showTPayPayPoAsPaymentMethod?: boolean;
  /** Enables TPay PayPal (a one-time redirect payment) for one-time PLN prices. */
  showTPayPayPalAsPaymentMethod?: boolean;
  /** Enables TPay installments (a one-time bank-financed payment) for one-time prices. */
  showTPayInstallments?: boolean;
  /** Which installment channels the checkout offers — see TPAY_INSTALLMENT_CHANNELS. */
  tpayInstallmentsChannelIds?: number[] | null;
  /**
   * Amount range (grosze, post-discount) the product accepts installments for.
   * `null` min falls back to DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT, `null` max is
   * unbounded — TPay documents no per-bank limits except PragmaPay's.
   */
  tpayInstallmentsMinAmount?: number | null;
  tpayInstallmentsMaxAmount?: number | null;
  /**
   * Per-channel wording for the checkout, keyed by channel id. Empty or missing
   * falls back to the channel's `customerLabel` — never to its internal `label`,
   * which carries our commission tier.
   */
  tpayInstallmentsChannelLabels?: Record<string, string> | null;
}

export enum LastNameFieldValues {
  Hidden = "hidden",
  ShowOptional = "optional",
  ShowRequired = "required",
}

export interface AutomationSettings {
  successUrl: string | null;
  showPromotionCodesField?: boolean;
  showPromotionCodesFieldOnlyForSubscription?: boolean;
  invoiceAsOption?: boolean;
  hideCountryField?: boolean;
  issueReceiptWhenInvoiceIsOptIn?: boolean;
  /**
   * For private individuals asking for an invoice, issue a receipt first and bind the
   * invoice to it (faktura do paragonu) instead of a standalone one.
   */
  issueInvoiceToReceiptForIndividuals?: boolean;
  sendInvoiceToClient?: boolean;
  disableEmailNotificationAboutPurchase?: boolean;
  disableEmailNotificationAboutPurchaseToOwner?: boolean;
  purchaseConfirmationAttachments?: { url: string; name: string; uid: string; contentType: string }[];
  allowUpgradeSubscriptionPlan?: boolean;
  allowDowngradeSubscriptionPlan?: boolean;
  embeddedForm?: EmbeddedForm & { pl?: EmbeddedForm };
  invoiceConfig?: ProductInvoiceConfig;
  cart?: ProductCartSettings;
  facebookPixelId?: string | null;
  googleAnalyticsId?: string | null;
  termsUrl?: string | null;
  privacyPolicyUrl?: string | null;
  claim?: string | null;
  showAdditionalInfoOptionalField?: boolean;
  showAdditionalInfoRequiredField?: boolean;
  showPhoneField?: boolean | null;
  showPhoneOptionalField?: boolean;
  showPhoneRequiredField?: boolean;
  showLastNameField?: LastNameFieldValues;
  metaConversionIntegrationId?: string | null;
  googleAnalyticsGtmId?: string | null;
  requireFullClientData?: boolean | null;
  /**
   * Kill-switch for the product's public checkout (`/c`, `/cart`, `/embed` — see
   * `isMainCheckoutPath`). Variant carts, funnels, renewals and the hidden sales-rep
   * checkout keep selling. TRE-1787.
   */
  disableMainCheckoutSales?: boolean;
}

export interface DiscordUser {
  id: string;
  username: string;
  avatar?: string;
  bot?: boolean;
  system?: boolean;
  locale?: string;
}

export interface Tags {
  bot_id: string;
}

export interface Role {
  id: string;
  name: string;
  permissions: string;
  position: number;
  color: number;
  hoist: boolean;
  managed: boolean;
  mentionable: boolean;
  icon: string | null;
  unicode_emoji: string | null;
  tags: Tags;
}

export interface DiscordGuild {
  id: string;
  name: string;
  icon: string;
  description: null | string;
  splash?: null | string;
  discovery_splash?: null | string;
  features: string[];
  emojis: string[];
  stickers: string[];
  banner?: null | string;
  owner_id: string;
  application_id: null | string;
  region: string;
  afk_channel_id?: string | null;
  afk_timeout: number;
  system_channel_id: string;
  widget_enabled: boolean;
  widget_channel_id?: string | null;
  verification_level: number;
  roles: Role[];
  default_message_notifications: number;
  mfa_level: number;
  explicit_content_filter: number;
  max_members: number;
  max_video_channel_users: number;
  premium_tier: number;
  premium_subscription_count: number;
  system_channel_flags: number;
  preferred_locale: string;
  rules_channel_id: string | null;
  public_updates_channel_id: string | null;
  hub_type?: string | null;
  premium_progress_bar_enabled: boolean;
  nsfw: boolean;
  nsfw_level: number;
}

export type DiscordIntegration = {
  guild: DiscordGuild;
  guildId: string;
  ownerId: string;
  createdAt: Date;
  updatedAt: Date;
};

export interface AppAutomationDocument {
  createdAt: Date;
  updatedAt: Date;
  webhooks: Webhook[];
  tasks: AutomationTask[];
  settings?: AutomationSettings;
  messages: ConstantMessage[];
  hasActiveWelcomeMessages: boolean;
  productIntegrations?: {
    [ProductIntegrationClient.Discord]?: DiscordIntegration[];
  };
}

export interface AutomatedEmail {
  subject: string;
  content: {
    html: string;
    json: string;
  };
}

export interface EmailsAutomation {
  afterPurchase: AutomatedEmail;
}

export type ProductAutomationEntity = Database["public"]["Tables"]["product_automation"]["Row"];

export interface ProductAutomation extends Omit<
  ProductAutomationEntity,
  "emails" | "tasks" | "productIntegrations" | "settings" | "webhooks"
> {
  owner: string;
  createdAt: string;
  updatedAt: string;
  productId: string;
  contactEmail: string | null;
  webhooks: Webhook[] | null;
  tasks: AutomationTask[] | null;
  settings: AutomationSettings;
  productIntegrations: {
    [ProductIntegrationClient.Discord]?: DiscordIntegration[];
  } | null;
  emails: EmailsAutomation | null;
}
